import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2, Send, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { enablePush, pushSupported } from "@/lib/push";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

const deviceName = (ua) => (/SamsungBrowser/.test(ua) ? "Samsung Internet" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Windows/.test(ua) ? "Windows" : /Mac/.test(ua) ? "Mac" : "—");

const Device = ({ d, result, onRemove }) => {
  const { t, fmtDate } = useI18n();
  const r = result || (d.last_status != null ? { ok: d.last_ok, status: d.last_status } : null);
  return (
    <li className="flex items-center gap-3 rounded-xl bg-black/30 px-3 py-2" data-testid={`push-device-${d.id}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-neutral-100">{deviceName(d.user_agent)} · <span className="text-neutral-400">{d.push_service}</span></p>
        <p className="text-xs text-neutral-500">{d.created_at ? (fmtDate ? fmtDate(d.created_at) : d.created_at.slice(0, 10)) : "—"}</p>
        {r && <p className={`text-xs ${r.ok ? "text-emerald-300" : "text-red-300"}`} data-testid={`push-device-result-${d.id}`}>{r.ok ? t("notif.delivered") : t("notif.failed", { s: r.status ?? "?" })}</p>}
      </div>
      <button type="button" className="rounded-full p-2 text-neutral-400 hover:bg-white/10 hover:text-red-300" onClick={() => onRemove(d.id)} aria-label={t("notif.remove")} data-testid={`push-device-remove-${d.id}`}>
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
};

export const PushDevices = () => {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/admin/notifications/devices").then(({ data }) => setList(data.devices)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const run = (key, fn) => async () => {
    setBusy(key);
    try { await fn(); } catch (err) { toast.error(err?.message === "denied" ? t("cap.pushDenied") : translateError(err, t)); } finally { setBusy(""); }
  };
  const enable = run("enable", async () => { await enablePush(); await load(); toast.success(t("cap.pushOn")); });
  const test = run("test", async () => {
    const { data } = await api.post("/admin/notifications/test");
    setResults(Object.fromEntries(data.results.map((r) => [r.id, r])));
    if (data.env === "preview") toast.info(t("notif.preview"));
    else if (!data.results.length) toast.error(t("notif.noDevice"));
    else toast[data.delivered ? "success" : "error"](t("notif.testResult", { n: data.delivered, total: data.results.length }));
    await load();
  });
  const remove = async (id) => {
    try { setList((await api.delete(`/admin/notifications/devices/${id}`)).data.devices); } catch (err) { toast.error(translateError(err, t)); }
  };
  return (
    <div className="mt-5 border-t border-white/10 pt-4" data-testid="push-devices">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-white"><BellRing className="h-4 w-4 text-[var(--gold)]" /> {t("notif.devices", { n: list.length })}</h3>
      <div className="mt-3 flex flex-wrap gap-2">
        {pushSupported() && <button type="button" className="btn-red btn-sm" onClick={enable} disabled={!!busy} data-testid="push-enable-here">{busy === "enable" ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} {t("notif.enableHere")}</button>}
        <button type="button" className="btn-gold btn-sm" onClick={test} disabled={!!busy} data-testid="push-test-button">{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 rtl:-scale-x-100" />} {t("notif.test")}</button>
      </div>
      {list.length ? <ul className="mt-3 space-y-2">{list.map((d) => <Device key={d.id} d={d} result={results[d.id]} onRemove={remove} />)}</ul>
        : <p className="mt-3 text-xs text-neutral-500" data-testid="push-devices-empty">{t("notif.noDevice")}</p>}
    </div>
  );
};
